<template>
  <v-select
    :class="{
      'v-select-danger': invalid === false,
      'select-size-sm': small,
    }"
    :value="value"
    :name="name"
    :options="paginatedValues"
    :disabled="isDisabled"
    :placeholder="computedPlaceholder"
    :selectable="opt => !opt.disabled"
    :reduce="opt => opt.value"
    :close-on-select="!multiple"
    :clearable="clearable"
    :taggable="taggable"
    :searchable="searchable"
    :filterable="filterable"
    :multiple="multiple"
    @search="onSearch"
    @click:clear="handleClearClick"
    @option:selecting="handleSelecting"
    @search:focus="onFocus"
    @input="onInput"
  >
    <template #spinner>
      <b-spinner
        v-if="isLoading"
        variant="primary"
        small
      />
    </template>
    <template #no-options>
      Escribe para buscar opciones
    </template>
    <li
      v-if="hasNextPage"
      slot="list-footer"
      class="pagination"
    >
      <button
        class="btn btn-block btn-primary btn-sm"
        type="button"
        @click="searchPage"
        :disabled="!hasNextPage"
      >
        buscar mas opciones...
      </button>
    </li>
  </v-select>
</template>

<script>
import _ from 'lodash';

import { listOptions } from '@/febos/servicios/api/opciones.api';
import { uniqueByField } from '@/utils';

const cache = {};
const promises = {};

export default {
  name: 'select-options-paginated',
  data() {
    return {
      query: '',
      limit: 1000,
      page: 1,
      pages: 1,
      opts: [],
      loadingOptions: false,
      filterBy: 'label'
    };
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingOptions;
    },
    computedPlaceholder() {
      if (this.isLoading) return 'Cargando opciones...';
      if (this.opts.length === 0) return 'No hay opciones disponibles';
      if (this.placeholder) return this.placeholder;
      return this.multiple ? 'Seleccione una o más opciones' : 'Seleccione una opción';
    },
    isDisabled() {
      return this.disabled;
    },
    hasNextPage() {
      return this.page < Math.max(this.pages, this.filteredPages);
    },
    filteredValues() {
      const toFilter = this.filterBy;
      let options = this.opts.filter((val) => new RegExp(`.*${this.query}.*`, 'i').test(val[toFilter]));
      if (this.showOptionAll) {
        options = [{
          value: '*',
          label: 'Todos'
        }, ...options];
      }
      return options;
    },
    paginatedValues() {
      return this.filteredValues.slice(0, this.limit * this.page);
    },
    filteredPages() {
      return this.filteredValues.length / this.limit;
    }
  },
  methods: {
    handleClearClick() {
      this.query = '';
      this.page = 1;
      this.pages = 1;
      this.opts = [];
      this.debounceFetchOptions({ page: 1, limit: 1000, query: '' });
    },
    onFocus() {
      this.page = 1;
      this.query = '';
      // this.debounceFetchOptions();
    },
    searchPage() {
      this.debounceFetchOptions({ page: this.page += 1, query: this.query });
    },
    onSearch(query) {
      if (!query) return;
      this.pages = 1;
      this.query = query;
      this.debounceFetchOptions({ query });
    },
    debounceFetchOptions: _.debounce(
      async function debounceFetchOptions(payload = { page: 1, limit: 1000 }) {
        this.loadingOptions = false;
        await this.tryFetchOptions(payload);
      }, 350
    ),
    handleSelecting(option) {
      if (this.multiple && this.value && this.value.length && this.value.includes(option.value)) {
        this.$emit('input', this.value.filter((v) => v !== option.value));
      }
    },
    async loadOptions() {
      this.loadingOptions = true;

      try {
        if (this.options && this.options.length > 0) {
          this.opts = this.options.map(this.mapper);
          return;
        }

        if (!this.optionsGroup) {
          this.loadingOptions = false;
          this.opts = [];
          return;
        }

        if (promises[this.optionsGroup]) {
          await promises[this.optionsGroup];
          this.opts = cache[this.optionsGroup];
          return;
        }

        if (cache[this.optionsGroup]) {
          this.opts = cache[this.optionsGroup];
          return;
        }

        promises[this.optionsGroup] = this.tryFetchOptions({ page: 1, limit: 1000 });
        await promises[this.optionsGroup];
      } finally {
        let option = null;

        if (this.defaultOption) {
          option = this.opts.find((opt) => opt.value === this.defaultOption);
        }

        if (this.opts.some((opt) => opt.default)) {
          option = this.opts.find((opt) => opt.default);
        }

        if (option) this.$emit('input', option.value);

        this.loadingOptions = false;
      }
    },
    async tryFetchOptions(payload) {
      this.loadingOptions = true;
      const { data } = await listOptions({
        grupoOpcion: this.optionsGroup,
        deshabilitado: 'no',
        orden: '+orden',
        pagina: payload.page || 1,
        itemsPorPagina: payload.limit || 1000,
        query: payload.query || '',
      });
      this.pages = data.totalPaginas;

      const parsedOpts = uniqueByField(data.opciones, 'valor').map((option) => {
        let extra;
        try {
          extra = JSON.parse(option.extra);
        } catch { /**/ }

        const def = extra.default || false;

        return {
          id: option.opcionId,
          value: option.valor,
          label: option.descripcion,
          order: option.orden,
          extra: extra || {},
          ...(def ? { default: true } : {}),
        };
      });

      let opts = this.extraOptions.concat(parsedOpts).map(this.mapper);

      if (this.alphabeticalSort) {
        opts = opts.sort((a, b) => a.label.localeCompare(b.label));
      }

      cache[this.optionsGroup] = opts;
      this.opts = opts;
      this.loadingOptions = false;
    },
    onInput(event) {
      this.$emit('input', event);
    },
  },
  created() {
    this.loadOptions();
  },
  watch: {
    options() {
      this.loadOptions();
    },
    query(newVal) {
      if (newVal === '') {
        this.handleClearClick();
      }
    }
  },
  props: {
    name: { type: String, required: true },
    invalid: { type: Boolean, default: null },
    validator: Object,
    small: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    value: { type: [String, Array], default: null },
    loading: { type: Boolean, default: false },

    optionsGroup: String,

    taggable: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    searchable: { type: Boolean, default: true },
    filterable: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    alphabeticalSort: { type: Boolean, default: false },

    mapper: {
      type: Function,
      default: (opt) => ({
        value: opt.value,
        label: opt.label,
        ...(opt.default ? { default: true } : {}),
      })
    },
    extraOptions: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    defaultOption: {
      type: [String, Object],
      default: null,
    },
  },
};
</script>

<style>
.vs__dropdown-option.vs__dropdown-option--selected.vs__dropdown-option--highlight {
  opacity: 0.95;
}
</style>
